import type { LocalIfcObservation } from "./local-ifc";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function records(value: unknown): readonly Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function boundedText(value: string | undefined): string | undefined {
  return value === undefined ? undefined : value.slice(0, 256);
}

function planCensus(raw: Record<string, unknown>): { readonly buildings: number; readonly floors: number; readonly walls: number } | null {
  if (!isRecord(raw.plan)) return null;
  const floors = records(raw.plan.floors);
  return {
    buildings: records(raw.plan.buildings).length,
    floors: floors.length,
    walls: floors.reduce((count, floor) => count + records(floor.walls).length, 0)
  };
}

function reportCensus(raw: Record<string, unknown>): {
  readonly importedFieldCount: number;
  readonly skipped: number;
  readonly geometryIssues: number;
  readonly expressIssues: number;
} | null {
  if (!isRecord(raw.report)) return null;
  return {
    importedFieldCount: isRecord(raw.report.imported) ? Object.keys(raw.report.imported).length : 0,
    skipped: records(raw.report.skipped).length,
    geometryIssues: records(raw.report.geometryIssues).length,
    expressIssues: records(raw.report.expressIssues).length
  };
}

function sessionCensus(stagedSession: LocalIfcObservation["stagedSession"]): {
  readonly present: boolean;
  readonly liveSessions: number | null;
  readonly aggregateStagedBytes: number | null;
  readonly aggregateStagedCapacity: number | null;
} {
  const stats = isRecord(stagedSession?.statsAfterClose) ? stagedSession.statsAfterClose : {};
  const value = isRecord(stats.value) ? stats.value : {};
  const global = isRecord(value.global) ? value.global : {};
  const numberOrNull = (value: unknown): number | null => (typeof value === "number" && Number.isFinite(value) ? value : null);
  return {
    present: stagedSession !== undefined,
    liveSessions: numberOrNull(global.liveSessions),
    aggregateStagedBytes: numberOrNull(global.aggregateStagedBytes),
    aggregateStagedCapacity: numberOrNull(global.aggregateStagedCapacity)
  };
}

export function localIfcDisplayReceipt(observation: LocalIfcObservation): Readonly<Record<string, unknown>> {
  const raw = isRecord(observation.raw) ? observation.raw : {};
  return {
    state: observation.state,
    ...(observation.reason === undefined ? {} : { reason: boundedText(observation.reason) }),
    source: { ...observation.source, filename: boundedText(observation.source.filename) },
    importer: observation.importer,
    outcomeCensus: observation.outcomeCensus,
    retainedEvidence: {
      scope: "complete observation object",
      availability: "UI session only",
      displayed: "bounded aggregate receipt",
      rawTopLevelFieldCount: Object.keys(raw).length,
      unknownTopLevelFieldCount: Object.keys(observation.unknownTopLevel).length,
      plan: planCensus(raw),
      report: reportCensus(raw)
    },
    stagedSession: sessionCensus(observation.stagedSession)
  };
}
